import type { ICustomerFulfillment } from "@/data/model/Customer";
import { Format } from "@/data/util/Format";

import { clientLanguage } from "@/shared/localization/language";
import { Ripple } from "@/shared/ui/Ripple";

import { appEvents } from "@/app/App";
import { TastierClient } from "@/app/TastierClient";
import type { AppContext } from "@/app/context/AppContext";
import { trans } from "@/app/localization/Trans";
import { CustomerFulfillmentHelper } from "@/app/util/CustomerFulfillmentHelper";
import {FulfillmentPositionDisplayMode} from "@/data/model/Enums";

export class PendingFulfillmentButton {
    public onClick?: { (fulfillment: ICustomerFulfillment) };

    private $element: JQuery;
    private timer?: number;
    private currentFulfillment?: ICustomerFulfillment;
    private context: AppContext;

    constructor() {
        this.context = TastierClient.context;

        this.$element = $('<div class="fulfillmentbtn" />');

        Ripple.bind(
            this.$element.click((e) => {
                e.preventDefault();

                if (this.currentFulfillment !== undefined && this.onClick !== undefined)
                    this.onClick(this.currentFulfillment);
            })
        );
    }

    public getCurrentFulfillment(): ICustomerFulfillment | undefined {
        return this.currentFulfillment;
    }

    public renderTo($element: JQuery): PendingFulfillmentButton {
        $element.append(this.$element);
        return this;
    }

    public startListening() {
        this.context.onFulfillmentsChanged.add(this.onFulfillmentsChanged, this);
        appEvents.OnSidenavVisibilityChanged.add(this.onShowHideOnSidenavVisibilityChange, this);

        this.onFulfillmentsChanged();
    }

    public stopListening() {
        this.context.onFulfillmentsChanged.remove(this.onFulfillmentsChanged, this);
        appEvents.OnSidenavVisibilityChanged.remove(this.onShowHideOnSidenavVisibilityChange, this);

        this.cancelTimer();
    }

    public destroy() {
        this.stopListening();
        this.$element.remove();
    }

    private onFulfillmentsChanged() {
        this.currentFulfillment = this.context.currentFulfillments
            .filter((c) => !c.isFulfilled)
            .sort((a, b) => {
                return -CustomerFulfillmentHelper.newestFirstComparator(a, b);
            })[0];

        if (this.currentFulfillment === undefined) {
            this.cancelTimer();
            this.$element.hide().empty();
        } else {
            this.setButtonToFulfillment(this.currentFulfillment);
            this.$element.show();
        }
    }

    private setButtonToFulfillment(fulfillment: ICustomerFulfillment) {
        this.$element.html(`<div class="name">${this.getFulfillmentName(fulfillment)}</div><div class="time"></div>`);
        this.updateTimeAndState();
        this.startTimer();
    }

    private getFulfillmentName(fulfillment: ICustomerFulfillment) {
        if (fulfillment.isLinked || (fulfillment.fulfillmentPositionDisplayMode === FulfillmentPositionDisplayMode.Position && fulfillment.currentQueuePosition == null)) {
            return trans.fulfillmentPopover.xItemsInMyOrder.replace("{0}", fulfillment.orderMainArticleQty.toString());
        } else {
            const name = fulfillment.name === undefined
                ? trans.fulfillmentPopover.emptyGroup
                : Format.translate(fulfillment.name, clientLanguage());
            return Format.htmlEscape(name);
        }
    }

    private onShowHideOnSidenavVisibilityChange(sidenavIsOpen: boolean) {
        if (sidenavIsOpen) {
            this.$element.stop(true, false).fadeOut(300, "easeOutExpo");
        } else {
            if (this.currentFulfillment !== undefined) this.$element.stop(true, false).fadeIn(300, "easeOutExpo");
        }
    }

    private cancelTimer() {
        if (this.timer === undefined) return;

        clearInterval(this.timer);
        this.timer = undefined;
    }

    private startTimer() {
        if (this.timer !== undefined) clearInterval(this.timer);

        this.timer = window.setInterval(() => {
            this.updateTimeAndState();
        }, 1000);
    }

    private updateTimeAndState() {
        if (this.currentFulfillment === undefined) {
            this.$element.find("> .time").empty();
            return;
        }

        const productionState = CustomerFulfillmentHelper.getFulfillmentProductionState(this.currentFulfillment);
        const newColor = CustomerFulfillmentHelper.colorsMap[productionState];
        const icon = CustomerFulfillmentHelper.getIcon(this.currentFulfillment, productionState);

        if (!this.$element.hasClass(newColor)) this.$element.removeClass("green red orange").addClass(newColor);

        const queueIndicator = CustomerFulfillmentHelper.getQueueIndicatorValue(this.currentFulfillment, productionState);
        this.$element
            .find("> .time")
            .html(queueIndicator === "" ? '' : `<i class="fal ${icon}"></i>${queueIndicator}`);
    }
}
